import React, { Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import QRCode from 'qrcode.react';

import { getAsset } from '../../../../../../../../util/assets';
import useComponentSize from '../../../../../../../../hooks/useComponentSize';
import getConfig from '../../../../../../../../util/config';
import { getEventSubtypeMarketingMaterialMetadata } from '../../AlbumFolderMarketingList/util';
import {
  MARKETING_MATERIAL_PRIMARY_TEXT_DISPLAY_ENUM,
  MARKETING_MATERIAL_SECONDARY_TEXT_DISPLAY_ENUM,
} from '../../../constants';
import Button from '../../../../../../../../components/global/Button';
import EditIcon from '../../../../../../../../components/icons/EditIcon';
import TrashIcon from '../../../../../../../../components/icons/TrashIcon';
import { getUtmParamsForQrCodeUrl } from '../helpers';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../../../../../constants/event-items';
import { getEventDateLabel } from '../../../../FreemiumEvents/SpecialtyAlbumGreetingModal/helpers';

const DEFAULT_CONFIGURATION = {
  primary_text_display: MARKETING_MATERIAL_PRIMARY_TEXT_DISPLAY_ENUM.EVENT_NAME,
  secondary_text_display:
    MARKETING_MATERIAL_SECONDARY_TEXT_DISPLAY_ENUM.EVENT_DATE_AND_LOCATION,
  secondary_text_personalized_message: '',
  display_watermark: false,
};

export const MARKETING_MATERIAL_EVENT_FLYER_CONFIGURATION_FIELD = {
  PRIMARY_TEXT_FIELD: 'PRIMARY_TEXT_FIELD',
  SECONDARY_TEXT_FIELD: 'SECONDARY_TEXT_FIELD',
  WATERMARK: 'WATERMARK',
};

export const MARKETING_MATERIAL_EVENT_FLYER_CONFIGURATION_ACTION = {
  EDIT: 'EDIT',
  DELETE: 'DELETE',
};

const propTypes = {
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
    onboarding_short_url: PropTypes.string,
    has_video_album: PropTypes.bool,
    is_web_gallery: PropTypes.bool,
  }),
  configuration: PropTypes.shape({
    primary_text_display: PropTypes.string,
    secondary_text_display: PropTypes.string,
    secondary_text_personalized_message: PropTypes.string,
    display_watermark: PropTypes.bool,
  }),
  configurationModeEnabled: PropTypes.bool,
  onFieldUpdate: PropTypes.func,
};

const AccountMarketingMaterialEventFlyerTemplate = ({
  albumFolder,
  configuration,
  onFieldUpdate,
  configurationModeEnabled,
}) => {
  const templateRef = useRef(null);
  const { width } = useComponentSize(templateRef);
  const parsedAlbumFolderName = albumFolder.name.replace('❤', '');

  const url =
    albumFolder.auto_contributor_share_url ??
    `${getConfig().app.baseUrl}/s/event-invite/${albumFolder.id}`;

  const config = configuration || DEFAULT_CONFIGURATION;

  const templateUrl =
    getEventSubtypeMarketingMaterialMetadata(albumFolder).template;

  const includeWatermark =
    !albumFolder?.purchased_event?.features
      ?.white_label_marketing_prints_enabled &&
    !albumFolder?.purchased_event_addon_types?.includes(
      EVENT_ADDON_ITEM_TYPE.WALDOFIED,
    ) &&
    // Covers Memory Book / GV
    !(
      albumFolder?.individual_albums &&
      albumFolder?.purchased_event_addon_types?.length > 0
    );

  const eventDateLabel = getEventDateLabel(albumFolder);

  const hasGV = albumFolder?.purchased_event_addon_types?.includes(
    EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
  );

  const solicitationCopy = (() => {
    if (albumFolder?.individual_albums && hasGV) {
      return (
        <>
          Scan me to add your <br /> video message
        </>
      );
    }

    return (
      <>
        Please send us <br /> your photos{' '}
        {(albumFolder?.has_video_album || hasGV) && <>&amp; videos!</>}
      </>
    );
  })();

  const showSubCopyBox = (() => {
    if (configurationModeEnabled) {
      return (
        config.secondary_text_display !==
        MARKETING_MATERIAL_SECONDARY_TEXT_DISPLAY_ENUM.NONE
      );
    }

    return (
      (!!eventDateLabel &&
        MARKETING_MATERIAL_SECONDARY_TEXT_DISPLAY_ENUM.EVENT_DATE_AND_LOCATION) ||
      (!!config.secondary_text_personalized_message &&
        MARKETING_MATERIAL_SECONDARY_TEXT_DISPLAY_ENUM.PERSONALIZED_MESSAGE)
    );
  })();

  return (
    <Fragment>
      <div
        style={{
          top: '49.5%',
          left: '35.1%',
          bottom: '27.3%',
          right: '35%',
        }}
        className="marketing-material-event-flyer-qrcode"
      >
        <QRCode
          renderAs="canvas"
          value={getUtmParamsForQrCodeUrl({
            url,
            albumFolder,
          })}
          size={width * 0.29 ?? 232}
        />
      </div>
      <img
        ref={templateRef}
        src={getAsset(templateUrl)}
        style={{ width: '100%' }}
        alt=""
      />
      {config.primary_text_display ===
        MARKETING_MATERIAL_PRIMARY_TEXT_DISPLAY_ENUM.EVENT_NAME && (
        <div className="marketing-material-event-flyer-event-name">
          <p>{parsedAlbumFolderName}</p>
          {configurationModeEnabled && (
            <Button
              className="marketing-material-configuration-action-button marketing-material-configuration-action-button-right"
              btnStyle="reset"
              onClick={() =>
                onFieldUpdate({
                  field:
                    MARKETING_MATERIAL_EVENT_FLYER_CONFIGURATION_FIELD.PRIMARY_TEXT_FIELD,
                  action:
                    MARKETING_MATERIAL_EVENT_FLYER_CONFIGURATION_ACTION.DELETE,
                })
              }
            >
              <div>
                <TrashIcon color="#ffffff" size={12} />
              </div>
            </Button>
          )}
        </div>
      )}
      {showSubCopyBox && (
        <div className="marketing-material-event-flyer-event-date">
          {configurationModeEnabled && (
            <>
              <Button
                className="marketing-material-configuration-action-button"
                btnStyle="reset"
                onClick={() =>
                  onFieldUpdate({
                    field:
                      MARKETING_MATERIAL_EVENT_FLYER_CONFIGURATION_FIELD.SECONDARY_TEXT_FIELD,
                    action:
                      MARKETING_MATERIAL_EVENT_FLYER_CONFIGURATION_ACTION.EDIT,
                  })
                }
              >
                <div>
                  <EditIcon color="#ffffff" size={12} />
                </div>
              </Button>
              <Button
                className="marketing-material-configuration-action-button marketing-material-configuration-action-button-right"
                btnStyle="reset"
                onClick={() =>
                  onFieldUpdate({
                    field:
                      MARKETING_MATERIAL_EVENT_FLYER_CONFIGURATION_FIELD.SECONDARY_TEXT_FIELD,
                    action:
                      MARKETING_MATERIAL_EVENT_FLYER_CONFIGURATION_ACTION.DELETE,
                  })
                }
              >
                <div>
                  <TrashIcon color="#ffffff" size={12} />
                </div>
              </Button>
            </>
          )}
          {config.secondary_text_display ===
            MARKETING_MATERIAL_SECONDARY_TEXT_DISPLAY_ENUM.EVENT_DATE_AND_LOCATION &&
            eventDateLabel && <p>{eventDateLabel}</p>}
          {config.secondary_text_display ===
            MARKETING_MATERIAL_SECONDARY_TEXT_DISPLAY_ENUM.PERSONALIZED_MESSAGE &&
            config.secondary_text_personalized_message && (
              <p>{config.secondary_text_personalized_message}</p>
            )}
        </div>
      )}

      <div className="marketing-material-event-flyer-solicitation">
        {solicitationCopy}
      </div>

      {includeWatermark && (
        <img
          src={getAsset('/powered-by-waldo-blue.svg')}
          alt=""
          style={{
            position: 'absolute',
            bottom: '2%',
            right: '50%',
            width: '10%',
            transform: 'translateX(50%)',
          }}
        />
      )}
    </Fragment>
  );
};

AccountMarketingMaterialEventFlyerTemplate.propTypes = propTypes;

export default AccountMarketingMaterialEventFlyerTemplate;
